// Barra contextual de estilo para la capa de texto seleccionada.

import { AlignCenter, AlignLeft, AlignRight, Bold, Italic, Minus, Plus } from 'lucide-react';
import { TEXT_FONT_OPTIONS } from '../lib/fonts';
import { ColorPicker } from './ColorPicker';
import type { SceneApi } from '../scene/useScene';
import type { TextAlign, TextLayer } from '../scene/types';

interface Props {
  api: SceneApi;
  brandColors?: string[];
}

export function TextToolbar({ api, brandColors }: Props) {
  const layer = api.selected;
  if (!layer || layer.type !== 'text') return null;
  const t = layer as TextLayer;
  const set = (patch: Partial<TextLayer>) => {
    api.checkpoint();
    api.updateLayer(t.id, patch);
  };
  const tgl = (on: boolean) =>
    `w-8 h-8 rounded-md flex items-center justify-center transition ${
      on ? 'bg-brand-primary text-white' : 'text-neutral-600 hover:bg-neutral-100'
    }`;

  return (
    <div className="h-full flex items-center gap-1.5 px-3 overflow-x-auto scrollbar-hide">
      <select
        value={t.fontFamily}
        onChange={(e) => set({ fontFamily: e.target.value })}
        className="h-8 shrink-0 rounded-md border border-neutral-200 bg-surface-card px-2 text-[13px] text-neutral-700 focus:border-brand-primary/50 focus:ring-2 focus:ring-brand-primary/20 outline-none"
      >
        {TEXT_FONT_OPTIONS.map((o) => (
          <option key={o.family} value={o.family}>
            {o.label ?? o.family}
          </option>
        ))}
      </select>

      <div className="flex items-center shrink-0 rounded-md border border-neutral-200">
        <button
          type="button"
          className="w-7 h-8 flex items-center justify-center text-neutral-600 hover:bg-neutral-100"
          onClick={() => set({ fontSizePct: Math.max(2, +(t.fontSizePct - 0.5).toFixed(1)) })}
          aria-label="Achicar"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="w-8 text-center text-[12px] tabular-nums text-neutral-700">
          {t.fontSizePct.toFixed(1)}
        </span>
        <button
          type="button"
          className="w-7 h-8 flex items-center justify-center text-neutral-600 hover:bg-neutral-100"
          onClick={() => set({ fontSizePct: Math.min(40, +(t.fontSizePct + 0.5).toFixed(1)) })}
          aria-label="Agrandar"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="w-px h-5 bg-neutral-200 shrink-0" />
      <button type="button" className={`${tgl(t.fontWeight === 'bold')} shrink-0`} title="Negrita" onClick={() => set({ fontWeight: t.fontWeight === 'bold' ? 'normal' : 'bold' })}>
        <Bold className="w-4 h-4" />
      </button>
      <button type="button" className={`${tgl(t.fontStyle === 'italic')} shrink-0`} title="Itálica" onClick={() => set({ fontStyle: t.fontStyle === 'italic' ? 'normal' : 'italic' })}>
        <Italic className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-neutral-200 shrink-0" />
      {(['left', 'center', 'right'] as TextAlign[]).map((a) => {
        const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight;
        return (
          <button key={a} type="button" className={`${tgl(t.align === a)} shrink-0`} title={a} onClick={() => set({ align: a })}>
            <Icon className="w-4 h-4" />
          </button>
        );
      })}

      <div className="w-px h-5 bg-neutral-200 shrink-0" />
      <ColorPicker
        value={t.color}
        onChange={(c) => set({ color: c })}
        triggerLabel="Color"
        brandColors={brandColors}
        align="right"
      />
    </div>
  );
}
